import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
    Routes,
} from "react-router-dom";
import { BASE_URL, REACT_APP_ENV, REACT_APP_VERSION } from "config";

const REPLAYS_ON_ERROR_SAMPLE_RATE =
  REACT_APP_ENV === "development" ? 0 :     // Do not capture replays on error in development
  REACT_APP_ENV === "sys"         ? 0.01 :  // Capture 1% of sessions with an error
  REACT_APP_ENV === "uat"         ? 0.05 :  // Capture 5% of sessions with an error
  REACT_APP_ENV === "production"  ? 0.1 :   // Capture 10% of sessions with an error
  0; // Default to not capturing any replays on error if the environment is not recognized

// Initialize Sentry with the react-router-v6 integration
// Needs to be called before any other Sentry or React DOM calls
export const initSentry = () => {
    Sentry.init({
        dsn: "https://6705082b10337ef261fc48c69fd88496@o4507310270840832.ingest.de.sentry.io/4508438949134416",
        // Set the release version based on the v{Year}.{WeekOfYear}.{DayOfWeek}.{Environment}
        release: REACT_APP_VERSION,
        environment: REACT_APP_ENV, // Set the environment based on the Azure DevOps pipeline
        integrations: [
            // Set up Sentry's integration with React Router v6
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for tracing.
        // Learn more at
        // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
        tracesSampleRate: 1.0,

        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: [/^\//, new RegExp(`^${BASE_URL}/api`)],

        // Do not capture Replay for sessions without errors,
        // Capture replays for sessions with an error at a rate depending on the environment
        // Learn more at
        // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
    });
};

// Export SentryRoutes for use in main app component routing
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Configure and export sentryReduxEnhancer to hook into Redux store
export const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    // Set the actionTransformer to redact sensitive data
    actionTransformer: (action) => {
        // List of actions that contain sensitive password data
        const sensitiveActions = [
            "SET_PASSWORD",
            "SIGN_IN",
            "app/auth/SET_PASSWORD",
            "app/auth/SIGN_IN"
        ];

        if (sensitiveActions.includes(action.type)) {
            // Create a copy of the action
            const sanitizedAction = {
                ...action,
                payload: {
                    ...action.payload
                }
            };

            // Remove password fields if they exist
            if (sanitizedAction.payload) {
                if ('password' in sanitizedAction.payload) {
                    sanitizedAction.payload.password = '[REDACTED]';
                }
                if ('newPassword' in sanitizedAction.payload) {
                    sanitizedAction.payload.newPassword = '[REDACTED]';
                }
                if ('confirmNewPassword' in sanitizedAction.payload) {
                    sanitizedAction.payload.confirmNewPassword = '[REDACTED]';
                }
            }

            return sanitizedAction;
        }

        return action;
    },
});